import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchGetAllServiciosBase,
  fetchAddPlan,
  fetchUpdatePLan,
  fetchGetPlanById,
  initializePlan,
  initializeMensaje,
} from "../../action";
import "../../css/styles.css";

const StyledTableHead = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#00a62b",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const FormPlan = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);

  const serviciosBase = useSelector((state) => state.app.itemServicios || []);
  const planState = useSelector((state) => state.app.plan);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      dispatch(initializeMensaje());
      await dispatch(fetchGetAllServiciosBase());
      if (isEdit && id) {
        await dispatch(fetchGetPlanById(id));
      } else {
        dispatch(initializePlan());
      }
      setIsLoading(false);
    };
    init();

    return () => {
      dispatch(initializePlan());
    };
  }, [dispatch, id, isEdit]);

  // Initial values setup
  let initialServiciosBaseMap = {};
  if (serviciosBase && Array.isArray(serviciosBase)) {
    serviciosBase.forEach((sb) => {
      if (
        isEdit &&
        planState &&
        planState.serviciosBase &&
        planState.serviciosBase.some((s) => s.id === sb.id)
      ) {
        initialServiciosBaseMap[sb.id] = JSON.stringify(sb);
      } else {
        initialServiciosBaseMap[sb.id] = "false";
      }
    });
  }

  const initialValues = {
    id: planState ? planState.id : "",
    nombre: planState ? planState.nombre || "" : "",
    esVenta: planState && planState.esVenta !== undefined ? planState.esVenta : true,
    fast: planState ? Boolean(planState.fast) : false,
    descuento: planState ? Boolean(planState.descuento) : false,
    textoDescuento: planState ? planState.textoDescuento || "" : "",
    serviciosBaseMap: initialServiciosBaseMap,
  };

  const validationSchema = Yup.object().shape({
    nombre: Yup.string().required("Ingrese el nombre del plan"),
    esVenta: Yup.boolean().required("Seleccione venta o arriendo"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Extract selected services
      const selectedServiciosBase = Object.values(values.serviciosBaseMap || {})
        .filter((val) => val && val !== "false")
        .map((val) => {
          let servi = JSON.parse(val);
          delete servi.rent;
          delete servi.sale;
          return servi;
        });

      if (selectedServiciosBase.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Atención",
          text: "Debe agregar al menos un servicio al plan",
        });
        setSubmitting(false);
        return;
      }

      const payload = {
        id: values.id || undefined,
        nombre: values.nombre,
        esVenta: Boolean(values.esVenta),
        fast: Boolean(values.fast),
        descuento: Boolean(values.descuento),
        textoDescuento: values.descuento ? values.textoDescuento : "",
        serviciosBase: selectedServiciosBase,
        serviciosAdicionales: [], // Only services contemplated
      };

      Swal.showLoading();
      if (isEdit) {
        await dispatch(fetchUpdatePLan(payload));
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Plan actualizado correctamente",
        }).then(() => {
          history.push("/planes");
        });
      } else {
        await dispatch(fetchAddPlan(payload));
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Plan creado correctamente",
        }).then(() => {
          history.push("/planes");
        });
      }
    } catch (error) {
      console.error("Error al guardar el plan", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al guardar el plan",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || (isEdit && !planState)) {
    return (
      <div className="contenido">
        <Grid container justifyContent="center" sx={{ my: 4 }}>
          <CircularProgress color="success" />
        </Grid>
      </div>
    );
  }

  return (
    <div className="contenido">
      <div className="header">
        <h2>{isEdit ? "Editar Plan" : "Agregar Nuevo Plan"}</h2>
      </div>

      <Box sx={{ mt: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/planes")}
        >
          Volver a la lista
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
            <Form>
              <Grid container spacing={3}>
                {/* Es Venta / Arriendo */}
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: "bold" }}>
                      Tipo de Plan
                    </FormLabel>
                    <RadioGroup
                      row
                      name="esVenta"
                      value={values.esVenta}
                      onChange={(e) =>
                        setFieldValue("esVenta", e.target.value === "true")
                      }
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio color="success" />}
                        label="Venta"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio color="success" />}
                        label="Arriendo"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Plan Fast */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.fast}
                        onChange={(e) => setFieldValue("fast", e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Typography style={{ fontWeight: 600 }}>
                        Plan Fast
                      </Typography>
                    }
                    sx={{ mt: 2 }}
                  />
                </Grid>

                {/* Nombre */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre del Plan"
                    name="nombre"
                    value={values.nombre}
                    onChange={handleChange}
                    error={touched.nombre && Boolean(errors.nombre)}
                    helperText={touched.nombre && errors.nombre}
                  />
                </Grid>


                {/* Descuento */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.descuento}
                        onChange={(e) =>
                          setFieldValue("descuento", e.target.checked)
                        }
                        color="success"
                      />
                    }
                    label="¿Aplica Descuento?"
                  />
                </Grid>

                {/* Texto Descuento */}
                {values.descuento && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Mensaje de descuento"
                      name="textoDescuento"
                      value={values.textoDescuento}
                      onChange={handleChange}
                    />
                  </Grid>
                )}

                {/* Tabla de Servicios (ÚNICAMENTE SERVICIOS) */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
                    Servicios (Selección)
                  </Typography>

                  {serviciosBase.length === 0 ? (
                    <Typography color="text.secondary">
                      No hay servicios registrados. Agregue servicios desde el mantenedor.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <StyledTableHead align="left">Servicio</StyledTableHead>
                            <StyledTableHead align="center" style={{ width: "100px" }}>
                              Sí
                            </StyledTableHead>
                            <StyledTableHead align="center" style={{ width: "100px" }}>
                              No
                            </StyledTableHead>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {serviciosBase.map((sb) => {
                            const currentValue = values.serviciosBaseMap[sb.id];
                            const isIncluded = currentValue && currentValue !== "false";

                            return (
                              <TableRow key={sb.id}>
                                <TableCell align="left">{sb.nombre}</TableCell>

                                {/* Radio Sí */}
                                <TableCell align="center">
                                  <Radio
                                    color="success"
                                    size="small"
                                    checked={isIncluded}
                                    onChange={() =>
                                      setFieldValue(
                                        `serviciosBaseMap.${sb.id}`,
                                        JSON.stringify(sb)
                                      )
                                    }
                                  />
                                </TableCell>

                                {/* Radio No */}
                                <TableCell align="center">
                                  <Radio
                                    color="default"
                                    size="small"
                                    checked={!isIncluded}
                                    onChange={() =>
                                      setFieldValue(
                                        `serviciosBaseMap.${sb.id}`,
                                        "false"
                                      )
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                {/* Actions */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isSubmitting}
                    size="large"
                  >
                    {isEdit ? "Guardar Cambios" : "Crear Plan"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </div>
  );
};

export default FormPlan;
